import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { LogoMark } from "./LogoMark";
export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const {
    pathname,
    hash
  } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const reduce = useReducedMotion();
  const navRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // Close the menu on navigation
  useEffect(() => setMenuOpen(false), [pathname, hash]);

  // Close on Escape (returning focus to the toggle) or on a click outside
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        toggleRef.current?.focus();
      }
    };
    const onPointer = (e: PointerEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointer);
    };
  }, [menuOpen]);

  // Once the page scrolls, the sticky bar gets a cream backing so it reads over content
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const links = [{
    label: "Home",
    to: "/",
    active: pathname === "/" && !["#now", "#about", "#projects", "#thoughts", "#receipts"].includes(hash)
  }, {
    label: "About",
    to: "/#about",
    active: pathname === "/" && hash === "#about"
  }, {
    label: "Selected Work",
    to: "/#projects",
    active: pathname === "/" && hash === "#projects" || pathname.startsWith("/work")
  }, {
    label: "Now",
    to: "/#now",
    active: pathname === "/" && hash === "#now"
  }, {
    label: "Receipts",
    to: "/#receipts",
    active: pathname === "/" && hash === "#receipts"
  }, {
    label: "Thoughts",
    to: "/#thoughts",
    active: pathname === "/" && hash === "#thoughts" || pathname.startsWith("/thoughts")
  }, {
    label: "Contact",
    to: `${pathname}#contact`,
    active: false
  }];
  return <header className={`fixed inset-x-0 top-0 z-30 transition-[background-color,box-shadow] duration-200 ease-out ${scrolled ? "bg-cream/90 shadow-[0_1px_0_rgba(29,24,20,0.08)] backdrop-blur-md" : "bg-transparent"}`} data-id="element-284" data-mp="18maee4czrd9q">
      <div className={`flex items-center justify-between px-6 transition-[padding] duration-200 ease-out md:px-12 lg:px-20 ${scrolled ? "py-3 md:py-4" : "pb-3 pt-6 md:pb-4 md:pt-10"}`} data-id="element-285" data-mp="13nz45sisb0qz">
        <Link to="/" aria-label="Alyssa Retza, home" className="flex items-center gap-3 text-dusty" data-id="element-286" data-mp="2y2jlbv7wce5m">
          <LogoMark className="h-9 w-9 shrink-0 md:h-11 md:w-11" data-id="element-287" data-mp="mf68skr9qs2u" />
          <span className="hidden whitespace-nowrap text-2xl font-medium tracking-[-0.02em] sm:inline md:text-4xl" data-id="element-288" data-mp="hgcw2ujt353q">
            Alyssa Retza
          </span>
        </Link>

        <nav ref={navRef} className="relative flex items-center gap-2 md:gap-4" aria-label="Primary" data-id="element-289" data-mp="rdrdawdjzxx3">
          <Link to={`${pathname}#contact`} className="hidden h-12 items-center gap-3 whitespace-nowrap rounded-full bg-espresso px-5 min-[400px]:flex text-sm font-medium uppercase text-cream ring-1 ring-cream/10 transition-colors duration-150 ease-out hover:bg-bark md:h-14 md:px-8 md:text-base" data-id="element-290" data-mp="154w4da2uvhvt">
            Let’s talk
            <span className="h-1.5 w-1.5 rounded-full bg-mustard" aria-hidden="true" data-id="element-291" data-mp="1a4sbvjarhrg4" />
          </Link>

          <button ref={toggleRef} type="button" onClick={() => setMenuOpen(o => !o)} aria-expanded={menuOpen} aria-controls="site-menu" className="flex h-12 items-center gap-3 whitespace-nowrap rounded-full bg-cream px-5 text-sm font-medium uppercase text-espresso ring-1 ring-espresso/10 transition-transform duration-150 ease-out active:scale-95 md:h-14 md:px-8 md:text-base" data-id="element-292" data-mp="3guysbv8ghg9p">
            {menuOpen ? "Close" : "Menu"}
            <span className="flex gap-1" aria-hidden="true" data-id="element-293" data-mp="2gwc8l0j50emi">
              <span className="h-1.5 w-1.5 rounded-full bg-dusty" data-id="element-294" data-mp="1k98lvw1k8ec0" />
              <span className="h-1.5 w-1.5 rounded-full bg-dusty" data-id="element-295" data-mp="1p70q9pje5sx3" />
            </span>
          </button>

          <AnimatePresence data-id="element-296" data-mp="1t940kaubqrgs">
            {menuOpen && <motion.div id="site-menu" initial={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: -8,
            scale: 0.96
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: -8,
            scale: 0.96
          }} transition={{
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} className="absolute right-0 top-full mt-3 w-max min-w-64 max-w-[calc(100vw-3rem)] origin-top-right rounded-3xl bg-cream p-3 text-espresso shadow-xl ring-1 ring-espresso/10" data-id="element-297" data-mp="1t7kjwj4raeeh">
                <ul data-id="element-298" data-mp="oy10thpp0i9a">
                  {links.map(link => <li key={link.label} data-id="element-299" data-mp="jzzfeq4so6un">
                      <Link to={link.to} onClick={() => {
                  setMenuOpen(false);
                  // Re-scroll even when the URL already has this hash
                  const [path, anchor] = link.to.split("#");
                  if (anchor && (path || "/") === pathname) {
                    document.getElementById(anchor)?.scrollIntoView({
                      behavior: "smooth"
                    });
                  } else if (!anchor && link.to === pathname) {
                    window.scrollTo({
                      top: 0,
                      behavior: "smooth"
                    });
                  }
                }} aria-current={link.active ? "page" : undefined} className="flex items-center justify-between gap-4 whitespace-nowrap rounded-2xl px-4 py-3 text-lg font-medium transition-colors duration-150 hover:bg-paper sm:text-2xl" data-id="element-300" data-mp="jz7k7gf40b4w">
                        {link.label}
                        {link.active && <span className="h-2 w-2 rounded-full bg-rust" aria-hidden="true" data-id="element-301" data-mp="e7eddtru5l1" />}
                      </Link>
                    </li>)}
                </ul>
              </motion.div>}
          </AnimatePresence>
        </nav>
      </div>
    </header>;
}